/**
 * Activity-specific difficulty / style tags for browse filters.
 * Ferrata: A–F (Alpine / Hüsler-style; C–F most common on real routes).
 * Rafting: WW1–WW5. Climbing: UIAA bands (with French / YDS inference).
 * Trail: easy→expert. Lake: swimming | nature.
 */
import type { EuropeHighlight, HighlightActivity } from "../types/europe-highlights";

export type DifficultyTag = string;

export type DifficultyOption = {
  tag: DifficultyTag;
  label: string;
};

/** Activities that expose a difficulty filter row in v1. */
export const DIFFICULTY_FILTER_ACTIVITIES: HighlightActivity[] = [
  "rafting",
  "climbing",
  "ferrata",
  "trail",
  "lake",
];

export const DIFFICULTY_OPTIONS: Partial<
  Record<HighlightActivity, DifficultyOption[]>
> = {
  rafting: [
    { tag: "ww1", label: "WW1" },
    { tag: "ww2", label: "WW2" },
    { tag: "ww3", label: "WW3" },
    { tag: "ww4", label: "WW4" },
    { tag: "ww5", label: "WW5" },
  ],
  ferrata: [
    { tag: "a", label: "A" },
    { tag: "b", label: "B" },
    { tag: "c", label: "C" },
    { tag: "d", label: "D" },
    { tag: "e", label: "E" },
    { tag: "f", label: "F" },
  ],
  climbing: [
    // UIAA Roman bands — labels also nod to French sport + YDS
    { tag: "uiaa-3", label: "I–III" },
    { tag: "uiaa-5", label: "IV–V" },
    { tag: "uiaa-7", label: "VI–VII" },
    { tag: "uiaa-9", label: "VIII–IX" },
    { tag: "uiaa-10", label: "X+" },
  ],
  trail: [
    { tag: "easy", label: "Easy" },
    { tag: "moderate", label: "Moderate" },
    { tag: "hard", label: "Hard" },
    { tag: "expert", label: "Expert" },
  ],
  lake: [
    { tag: "swimming", label: "Swimming" },
    { tag: "nature", label: "Nature / scenic" },
  ],
};

function collectText(h: EuropeHighlight): string {
  const facts = (h.facts || [])
    .map((f) => `${f.key} ${f.value}`)
    .join(" ");
  const features = (h.features || []).join(" ");
  return `${h.summary || ""} ${facts} ${features}`.toLowerCase();
}

function factValue(h: EuropeHighlight, ...keys: string[]): string {
  const want = new Set(keys.map((k) => k.toLowerCase()));
  for (const f of h.facts || []) {
    if (want.has((f.key || "").toLowerCase())) {
      return (f.value || "").toLowerCase();
    }
  }
  return "";
}

/** Map K1–K5 → A–E (K5 ≈ E; F only from explicit F). */
function kToLetter(n: number): DifficultyTag | null {
  if (n < 1 || n > 5) return null;
  return String.fromCharCode(96 + n); // 1→a … 5→e
}

function parseFerrataTags(text: string, difficultyFact: string): DifficultyTag[] {
  const tags = new Set<DifficultyTag>();
  const src = `${difficultyFact} ${text}`;

  const addLetterRange = (a: string, b?: string) => {
    const start = a.toLowerCase().charCodeAt(0);
    const end = b ? b.toLowerCase().charCodeAt(0) : start;
    const lo = Math.min(start, end);
    const hi = Math.max(start, end);
    for (let c = lo; c <= hi; c++) {
      if (c >= 97 && c <= 102) tags.add(String.fromCharCode(c));
    }
  };

  // C/D, C–E, grade D, VF C
  const letterRe =
    /(?:^|[^a-z])(?:grade\s*|klasse\s*|niveau\s*|vf\s*|~)?([a-f])(?:\s*[\/–\-]\s*([a-f]))?(?=[^a-z]|$)/gi;
  let m: RegExpExecArray | null;
  while ((m = letterRe.exec(src))) {
    addLetterRange(m[1], m[2]);
  }

  const kRe = /\bk\s*([1-5])(?:\s*[–\-\/]\s*k?\s*([1-5]))?\b/gi;
  while ((m = kRe.exec(src))) {
    const a = kToLetter(Number(m[1]));
    const b = m[2] ? kToLetter(Number(m[2])) : null;
    if (a && b) addLetterRange(a, b);
    else if (a) tags.add(a);
  }

  if (/family|beginner|easy|leicht|facile/.test(src) && !tags.size) {
    tags.add("a");
    tags.add("b");
  }
  if (/moderate|mittel|pd\b/.test(src) && !tags.size) {
    tags.add("b");
    tags.add("c");
  }
  if (
    /hard|difficult|schwer|commitment|exposed|exposure|expert|sehr schwer/.test(
      src
    ) &&
    !tags.size
  ) {
    tags.add("d");
    tags.add("e");
  }

  return [...tags];
}

function parseWwTags(text: string, difficultyFact: string): DifficultyTag[] {
  const tags = new Set<DifficultyTag>();
  const src = `${difficultyFact} ${text}`;

  const roman: Record<string, number> = {
    i: 1,
    ii: 2,
    iii: 3,
    iv: 4,
    v: 5,
  };
  const toN = (s: string): number | null => {
    const t = s.toLowerCase();
    if (/^[1-5]$/.test(t)) return Number(t);
    return roman[t] || null;
  };

  const addRange = (a: number | null, b: number | null) => {
    if (!a) return;
    const end = b && b >= a ? b : a;
    for (let i = a; i <= end; i++) tags.add(`ww${i}`);
  };

  // WW2–WW4 / WW 2-4 / class III–IV
  const rangeRe =
    /\b(?:ww|class|klasse)?\s*([1-5]|i{1,3}|iv|v)\s*[–\-\/]\s*(?:ww|class|klasse)?\s*([1-5]|i{1,3}|iv|v)\b/gi;
  let m: RegExpExecArray | null;
  while ((m = rangeRe.exec(src))) {
    addRange(toN(m[1]), toN(m[2]));
  }

  // Single WW3 / WW 3
  for (let i = 1; i <= 5; i++) {
    if (new RegExp(`\\bww\\s*${i}\\b`, "i").test(src)) tags.add(`ww${i}`);
  }

  return [...tags];
}

function parseBucketTags(text: string, difficultyFact: string): DifficultyTag[] {
  const tags = new Set<DifficultyTag>();
  const src = `${difficultyFact} ${text}`;

  if (/\beasy\b|leicht|beginner|family|diff\b|moderate\s*diff|vd\b|v\.?diff/.test(src)) {
    tags.add("easy");
  }
  if (/\bmoderate\b|mittel|mod\b|severe\b|hs\b|vs\b|4[a-c]\b|5\.[0-7]/.test(src)) {
    tags.add("moderate");
  }
  if (/\bhard\b|difficult|schwer|hvs\b|e1\b|e2\b|5\.[8-9]|6[a-b]\b|multi-day|remote/.test(src)) {
    tags.add("hard");
  }
  if (
    /\bexpert\b|extreme|serious|e[3-9]\b|alpine ridge|winter|commitment|technical|6[c-d]|7[a-c]|muy\s*duro/.test(
      src
    )
  ) {
    tags.add("expert");
  }

  // Ranges like "Easy–moderate"
  if (/easy\s*[–\-]\s*moderate/.test(src)) {
    tags.add("easy");
    tags.add("moderate");
  }
  if (/easy\s*[–\-]\s*hard/.test(src)) {
    tags.add("easy");
    tags.add("moderate");
    tags.add("hard");
  }
  if (/moderate\s*[–\-]\s*hard/.test(src)) {
    tags.add("moderate");
    tags.add("hard");
  }
  if (/mod\s*[–\-]\s*e5/.test(src)) {
    tags.add("moderate");
    tags.add("hard");
    tags.add("expert");
  }
  if (/diff\s*[–\-]\s*e/.test(src)) {
    tags.add("easy");
    tags.add("moderate");
    tags.add("hard");
    tags.add("expert");
  }

  return [...tags];
}

const UIAA_ROMAN: Record<string, number> = {
  i: 1,
  ii: 2,
  iii: 3,
  iv: 4,
  v: 5,
  vi: 6,
  vii: 7,
  viii: 8,
  ix: 9,
  x: 10,
  xi: 11,
  xii: 12,
};

/** Map a numeric UIAA grade (1–12) into filter band tags. */
function uiaaBandsFromGrade(n: number): DifficultyTag[] {
  if (n <= 3) return ["uiaa-3"];
  if (n <= 5) return ["uiaa-5"];
  if (n <= 7) return ["uiaa-7"];
  if (n <= 9) return ["uiaa-9"];
  return ["uiaa-10"];
}

function addUiaaRange(tags: Set<DifficultyTag>, a: number, b?: number) {
  const end = b && b >= a ? b : a;
  for (let i = a; i <= end; i++) {
    for (const t of uiaaBandsFromGrade(i)) tags.add(t);
  }
}

/** French / sport number → approximate UIAA grade for banding. */
function frenchToUiaa(n: number, letter?: string): number {
  if (n <= 3) return 2;
  if (n === 4) return 3;
  if (n === 5) return letter === "c" ? 5 : 4;
  if (n === 6) {
    if (letter === "a") return 6;
    if (letter === "b") return 7;
    return 7;
  }
  if (n === 7) {
    if (letter === "a") return 8;
    if (letter === "b") return 8;
    return 9;
  }
  if (n === 8) return letter === "a" ? 9 : 10;
  return 11;
}

/**
 * Climbing grades → UIAA filter bands.
 * Accepts UIAA Roman (IV–VI), French sport (6a–7c / bare 4–7),
 * Fontainebleau, UK trad, YDS (5.10a–5.12).
 */
function parseClimbingTags(text: string, difficultyFact: string): DifficultyTag[] {
  const tags = new Set<DifficultyTag>();
  const fact = (difficultyFact || "").toLowerCase().trim();
  const src = `${fact} ${text}`.toLowerCase();

  // UIAA Roman: UIAA VI, VI+, IV–VII, grade V–
  const uiaaRe =
    /\b(?:uiaa|uiia|uiiaa)?\s*(i{1,3}|iv|v|vi{0,3}|ix|x{1,2})(\+|-|\/[a-z])?(?:\s*[–\-\/]\s*(i{1,3}|iv|v|vi{0,3}|ix|x{1,2})(\+|-)?)?\b/gi;
  let m: RegExpExecArray | null;
  while ((m = uiaaRe.exec(src))) {
    // Skip bare "i" noise unless UIAA context or grade-like surroundings
    const token = m[1].toLowerCase();
    const around = src.slice(Math.max(0, m.index - 10), m.index + m[0].length + 6);
    if (
      token === "i" &&
      !/uiaa|uiia|grade|schwier|klettern/.test(around) &&
      !m[3]
    ) {
      continue;
    }
    const a = UIAA_ROMAN[token];
    const b = m[3] ? UIAA_ROMAN[m[3].toLowerCase()] : null;
    if (a) addUiaaRange(tags, a, b || undefined);
  }

  // Numeric ranges on the difficulty fact (WP: "4 - 7" French; curated: "3–11" UIAA)
  if (fact) {
    const numRange =
      /\b([1-9]|1[0-2])([a-c])?\+?\s*[–\-]\s*([1-9]|1[0-2])([a-c])?\+?\b/gi;
    while ((m = numRange.exec(fact))) {
      const lo = Number(m[1]);
      const hi = Number(m[3]);
      // UIAA scales run to 10–12; sport French stays in 3–9
      const looksUiaa = hi >= 10 || lo >= 10;
      if (looksUiaa) {
        addUiaaRange(tags, Math.min(lo, hi), Math.max(lo, hi));
      } else {
        addUiaaRange(
          tags,
          frenchToUiaa(lo, m[2] || undefined),
          frenchToUiaa(hi, m[4] || undefined)
        );
      }
    }
  }

  // French sport: 4, 5a, 6b+, 7c/8a (body text — letter or grade context required)
  const frRe =
    /\b([4-9])([a-c])?(\+)?(?:\s*[–\-\/]\s*([4-9])([a-c])?(\+)?)?\b/gi;
  while ((m = frRe.exec(src))) {
    const around = src.slice(Math.max(0, m.index - 8), m.index + m[0].length + 4);
    const inFact = fact && m.index < fact.length + 1;
    if (
      !m[2] &&
      !inFact &&
      !/fr|french|sport|grade|uiaa|yds|5\./.test(around)
    ) {
      continue;
    }
    const a = frenchToUiaa(Number(m[1]), m[2] || undefined);
    const b = m[4]
      ? frenchToUiaa(Number(m[4]), m[5] || undefined)
      : undefined;
    addUiaaRange(tags, a, b);
  }

  // Fontainebleau / Font: Font 3–8B, Fb 4–8A
  const fontRe =
    /\b(?:font|fb|fontainebleau)\s*([1-8])([a-c])?\+?(?:\s*[–\-]\s*([1-8])([a-c])?\+?)?\b/gi;
  while ((m = fontRe.exec(src))) {
    // Font ≈ French sport banding for filter purposes
    const a = frenchToUiaa(Number(m[1]), m[2] || undefined);
    const b = m[3]
      ? frenchToUiaa(Number(m[3]), m[4] || undefined)
      : undefined;
    addUiaaRange(tags, a, b);
  }

  // UK trad: Diff–E8, Mod–Severe, VS–E5
  if (
    /\b(?:mod|diff|v\.?\s*diff|vd|severe|hs|vs|hvs|e[1-9])\b/.test(src)
  ) {
    if (/\b(?:mod|diff|v\.?\s*diff|vd)\b/.test(src)) tags.add("uiaa-3");
    if (/\b(?:severe|hs|vs)\b/.test(src)) tags.add("uiaa-5");
    if (/\bhvs\b|\be[1-3]\b/.test(src)) tags.add("uiaa-7");
    if (/\be[4-6]\b/.test(src)) tags.add("uiaa-9");
    if (/\be[7-9]\b/.test(src)) tags.add("uiaa-10");
  }

  // YDS: 5.6, 5.10a, 5.12d–5.13a
  const ydsRe =
    /\b5\.([0-9]|1[0-5])([a-d])?(?:\s*[–\-\/]\s*5\.([0-9]|1[0-5])([a-d])?)?\b/gi;
  while ((m = ydsRe.exec(src))) {
    const toUiaa = (maj: number): number => {
      if (maj <= 6) return 3;
      if (maj <= 8) return 4;
      if (maj === 9) return 5;
      if (maj === 10) return 6;
      if (maj === 11) return 7;
      if (maj === 12) return 8;
      if (maj === 13) return 9;
      return 10;
    };
    const a = toUiaa(Number(m[1]));
    const b = m[3] ? toUiaa(Number(m[3])) : undefined;
    addUiaaRange(tags, a, b);
  }

  // Legacy easy/hard words → bands
  if (/\beasy\b|beginner|family|leichter/.test(src)) tags.add("uiaa-3");
  if (/\bmoderate\b|intermediate|mittel/.test(src)) tags.add("uiaa-5");
  if (/\bhard\b|difficult|schwer|advanced/.test(src)) tags.add("uiaa-7");
  if (/\bexpert\b|elite|extreme|pro\b/.test(src)) {
    tags.add("uiaa-9");
    tags.add("uiaa-10");
  }

  return [...tags];
}

/** Map old trail-style ?diff=easy onto climbing UIAA bands. */
const CLIMBING_DIFF_ALIASES: Record<string, DifficultyTag[]> = {
  easy: ["uiaa-3"],
  moderate: ["uiaa-5"],
  hard: ["uiaa-7"],
  expert: ["uiaa-9", "uiaa-10"],
};

function parseLakeTags(text: string): DifficultyTag[] {
  const tags = new Set<DifficultyTag>();
  if (/swim|bathing|beach|lido|wild\s*swim/.test(text)) tags.add("swimming");
  if (
    /scenic|viewpoint|alpine\s*lake|loch|nature|quiet|deep|shoreline|national\s*park|mirror/.test(
      text
    )
  ) {
    tags.add("nature");
  }
  // Default scenic lakes without swim cues
  if (!tags.size && /lake|loch|lac |lago /.test(text)) tags.add("nature");
  return [...tags];
}

/**
 * Difficulty / style tags for a highlight, inferred for the given activity lens.
 * When activity is omitted, uses primaryType.
 */
export function getHighlightDifficultyTags(
  highlight: EuropeHighlight,
  activity?: HighlightActivity | null
): DifficultyTag[] {
  const act = (activity || highlight.primaryType) as HighlightActivity;
  if (!DIFFICULTY_OPTIONS[act]) return [];

  const text = collectText(highlight);
  const difficultyFact = factValue(
    highlight,
    "difficulty",
    "grades",
    "grade",
    "ww",
    "whitewater",
    "class",
    "type"
  );

  if (act === "ferrata") return parseFerrataTags(text, difficultyFact);
  if (act === "rafting") return parseWwTags(text, difficultyFact);
  if (act === "lake") return parseLakeTags(text);
  if (act === "climbing") return parseClimbingTags(text, difficultyFact);
  if (act === "trail") return parseBucketTags(text, difficultyFact);
  return [];
}

/** True if highlight matches any selected difficulty tag (for primary activity). */
export function highlightMatchesDifficulty(
  highlight: EuropeHighlight,
  activity: HighlightActivity,
  selected: DifficultyTag[]
): boolean {
  if (!selected.length) return true;
  const tags = getHighlightDifficultyTags(highlight, activity);
  // Community points without grade facts stay visible (don't hide for missing data)
  if (!tags.length) return highlight.source === "wordpress";
  const want = new Set(selected.map((t) => t.toLowerCase()));
  return tags.some((t) => want.has(t.toLowerCase()));
}

export function parseDifficultyQuery(
  search: string,
  activity: HighlightActivity | null
): DifficultyTag[] {
  if (!search || !activity) return [];
  const allowed = new Set(
    (DIFFICULTY_OPTIONS[activity] || []).map((o) => o.tag)
  );
  if (!allowed.size) return [];
  const raw = new URLSearchParams(search).get("diff") || "";
  const out = new Set<DifficultyTag>();
  for (const part of raw.split(",").map((s) => s.trim().toLowerCase())) {
    if (!part) continue;
    if (allowed.has(part)) {
      out.add(part);
      continue;
    }
    if (activity === "climbing" && CLIMBING_DIFF_ALIASES[part]) {
      for (const t of CLIMBING_DIFF_ALIASES[part]) out.add(t);
    }
  }
  return Array.from(out);
}

/** Build query string preserving also= and updating diff=. */
export function buildActivitySearchQuery(
  also: HighlightActivity[],
  diff: DifficultyTag[]
): string {
  const params = new URLSearchParams();
  if (also.length) params.set("also", also.join(","));
  if (diff.length) params.set("diff", diff.join(","));
  const s = params.toString();
  return s ? `?${s}` : "";
}
